import { FC, useContext } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import useImageUploader from "../../../../components/ui/ImageUploader";
import { AsyncSelect, Form, FormField, ModalDialog } from "@rothoblaas/design-system";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useAsyncOptions from "../../../../hooks/useAsyncOptions";
import useI18n from "../../../../hooks/useTranslations";
import { FasteningType } from "../../../../http/deckDesigner/types/fasteningTypes";
import { GroovingType } from "../../../../http/deckDesigner/types/groovingTypes";
import useDeckFasteningTypesApi from "../../../../http/deckDesigner/useFasteningTypes";
import useDeckGroovingTypesApi from "../../../../http/deckDesigner/useGroovingTypes";
import TranslatableTextField from "../../../../components/ui/TranslatableTextField";

interface FasteningTypeFormBody {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  groovingTypes: GroovingType[];
}

interface FasteningTypeFormProps {
  fasteningType: FasteningType;
  onClose: () => void;
}

const FasteningTypeForm: FC<FasteningTypeFormProps> = ({ fasteningType, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const fasteningTypesApi = useDeckFasteningTypesApi();
  const groovingTypesApi = useDeckGroovingTypesApi();
  const groovingTypeOptions = useAsyncOptions(groovingTypesApi.getDeckGroovingTypes);

  const defaultValues: FasteningTypeFormBody = {
    name: fasteningType.name ?? null,
    nameIt: fasteningType.nameIt ?? null,
    nameDe: fasteningType.nameDe ?? null,
    nameEs: fasteningType.nameEs ?? null,
    nameFr: fasteningType.nameFr ?? null,
    groovingTypes: fasteningType.groovingTypes ?? [],
  };

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: fasteningType.image ?? undefined,
    isLoading: false,
  });

  const onSubmit = () => async (formData: FasteningTypeFormBody) => {
    try {
      const image = await fasteningTypesApi.uploadImage(fasteningType.code, newImage, removeImage);
      await fasteningTypesApi.updateDeckFasteningType(fasteningType.code, {
        name: formData.name ?? "",
        nameIt: formData.nameIt || null,
        nameDe: formData.nameDe || null,
        nameEs: formData.nameEs || null,
        nameFr: formData.nameFr || null,
        groovingTypes: formData.groovingTypes,
        ...image,
      });

      onSave();
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={`${i18n.translation.common.edit} ${i18n.translation.deckDesignerModule.fasteningTypes.singular}`}
    >
      <Form<FasteningTypeFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {(form) => (
          <FormFieldsContainer fullWidth imageUploader={component}>
            <TranslatableTextField form={form} />
            <Controller
              name="groovingTypes"
              control={form.control}
              rules={{ validate: (value) => !!value.length }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.deckDesignerModule.groovingTypes.plural} error={fieldState.error}>
                  {({ labelId }) => (
                    <AsyncSelect<GroovingType, true>
                      {...groovingTypeOptions}
                      inputId={labelId}
                      value={field.value}
                      isMulti
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.code}
                      isInvalid={fieldState.invalid}
                      onChange={(options) => field.onChange(Array.from(options))}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default FasteningTypeForm;
